import { useEffect, useState, type FormEvent } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import { ApiError } from '../../shared/api/client.ts';
import { useCadastrarUsuario, useEditarUsuario } from '../../shared/api/queries.ts';
import type { Papel, Usuario } from '../../shared/api/tipos.ts';
import { useSessao } from '../auth/sessao.tsx';

const TAMANHO_MINIMO_SENHA = 8;

const VAZIO = {
  nome: '',
  usuario: '',
  senha: '',
  papel: 'usuario' as Papel,
  ativo: true,
};

const DESCRICAO_PAPEL: Readonly<Record<Papel, string>> = {
  administrador: 'Acessa tudo, inclusive esta tela de usuários.',
  usuario: 'Acessa clientes e contabilidades.',
  contador:
    'Acesso de fora do escritório: só os clientes da contabilidade dele, e só para ' +
    'consultar e baixar. Criado na tela da contabilidade.',
};

export function DialogoUsuario({
  aberto,
  usuario,
  aoFechar,
}: {
  aberto: boolean;
  usuario?: Usuario | undefined;
  aoFechar: () => void;
}) {
  const editando = usuario !== undefined;
  const { usuario: eu } = useSessao();
  const souEu = editando && eu?.id === usuario.id;

  const [campos, setCampos] = useState(VAZIO);

  const cadastrar = useCadastrarUsuario();
  const editar = useEditarUsuario();

  const salvando = cadastrar.isPending || editar.isPending;
  const erro = cadastrar.error ?? editar.error;

  useEffect(() => {
    if (!aberto) return;

    setCampos(
      usuario === undefined
        ? VAZIO
        : {
            nome: usuario.nome,
            usuario: usuario.usuario,
            // Em branco mantém a senha atual: a tela nunca recebe a senha de
            // ninguém, nem um valor de enfeite para o campo.
            senha: '',
            papel: usuario.papel,
            ativo: usuario.ativo,
          },
    );
    cadastrar.reset();
    editar.reset();
    // Rodar na abertura e na troca de usuário é a intenção; incluir as
    // mutações limparia o erro assim que ele aparecesse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto, usuario]);

  const nomeOk = campos.nome.trim().length >= 3;

  // Mesmo formato do domínio, só para a resposta ser imediata: letras sem
  // acento, números, ponto, hífen e sublinhado, começando por letra ou número.
  const usuarioOk = /^[a-z0-9][a-z0-9._-]{2,39}$/.test(campos.usuario);
  const senhaObrigatoria = !editando;
  const senhaOk =
    campos.senha === ''
      ? !senhaObrigatoria
      : campos.senha.length >= TAMANHO_MINIMO_SENHA;

  const podeEnviar = nomeOk && usuarioOk && senhaOk && !salvando;

  function fechar() {
    setCampos(VAZIO);
    cadastrar.reset();
    editar.reset();
    aoFechar();
  }

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (!podeEnviar) return;

    try {
      if (usuario === undefined) {
        await cadastrar.mutateAsync({
          nome: campos.nome.trim(),
          usuario: campos.usuario,
          senha: campos.senha,
          papel: campos.papel,
        });
      } else {
        await editar.mutateAsync({
          id: usuario.id,
          nome: campos.nome.trim(),
          usuario: campos.usuario,
          papel: campos.papel,
          ativo: campos.ativo,
          senha: campos.senha === '' ? undefined : campos.senha,
        });
      }
      fechar();
    } catch {
      // A mensagem já está em `erro`; o diálogo fica aberto para corrigir.
    }
  }

  return (
    <Dialog
      open={aberto}
      onClose={salvando ? undefined : fechar}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { component: 'form', onSubmit: enviar } }}
    >
      <DialogTitle>{editando ? 'Editar usuário' : 'Novo usuário'}</DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {erro !== null && <Alert severity="error">{mensagemDoErro(erro)}</Alert>}

          <TextField
            label="Nome"
            value={campos.nome}
            onChange={(evento) => setCampos((atual) => ({ ...atual, nome: evento.target.value }))}
            required
            autoFocus
            fullWidth
            slotProps={{ htmlInput: { maxLength: 120 } }}
          />

          <TextField
            label="Usuário"
            value={campos.usuario}
            onChange={(evento) =>
              setCampos((atual) => ({
                // Minúsculas na digitação, como o domínio normalizaria: assim o
                // que se vê é o que vai ser gravado.
                ...atual,
                usuario: evento.target.value.toLowerCase().replace(/\s/g, ''),
              }))
            }
            required
            fullWidth
            autoComplete="off"
            error={campos.usuario !== '' && !usuarioOk}
            helperText={
              campos.usuario !== '' && !usuarioOk
                ? 'De 3 a 40 caracteres: letras sem acento, números, ponto, hífen e sublinhado.'
                : 'É o que a pessoa digita para entrar.'
            }
            slotProps={{
              htmlInput: {
                maxLength: 40,
                autoCapitalize: 'none',
                autoCorrect: 'off',
                spellCheck: false,
              },
            }}
          />

          <TextField
            label={editando ? 'Nova senha' : 'Senha'}
            type="password"
            value={campos.senha}
            onChange={(evento) => setCampos((atual) => ({ ...atual, senha: evento.target.value }))}
            required={senhaObrigatoria}
            fullWidth
            autoComplete="new-password"
            error={campos.senha !== '' && campos.senha.length < TAMANHO_MINIMO_SENHA}
            helperText={
              editando
                ? 'Deixe em branco para manter a senha atual. Trocar encerra as sessões dele.'
                : `Ao menos ${TAMANHO_MINIMO_SENHA} caracteres.`
            }
          />

          <TextField
            label="Papel"
            select
            value={campos.papel}
            onChange={(evento) =>
              setCampos((atual) => ({ ...atual, papel: evento.target.value as Papel }))
            }
            fullWidth
            helperText={DESCRICAO_PAPEL[campos.papel]}
          >
            <MenuItem value="usuario">Usuário</MenuItem>
            <MenuItem value="administrador">Administrador</MenuItem>
            {/* `contador` não é oferecido aqui: ele nasce preso a uma
                contabilidade, e é a tela dela que tem esse dado na mão. A
                opção aparece só para não sumir o papel de quem já é um — e
                desmarcá-la solta o vínculo, no servidor. */}
            {campos.papel === 'contador' && <MenuItem value="contador">Contador</MenuItem>}
          </TextField>

          {editando && (
            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={campos.ativo}
                    onChange={(evento) =>
                      setCampos((atual) => ({ ...atual, ativo: evento.target.checked }))
                    }
                  />
                }
                label="Acesso ativo"
              />
              <FormHelperText sx={{ mt: 0 }}>
                {souEu
                  ? 'Este é o seu próprio acesso — desmarcar tira você do sistema.'
                  : 'Desmarcar encerra as sessões abertas e impede novos logins. Nada é apagado.'}
              </FormHelperText>
            </div>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={fechar} disabled={salvando}>
          Cancelar
        </Button>
        <Button type="submit" variant="contained" disabled={!podeEnviar} loading={salvando}>
          {editando ? 'Salvar' : 'Cadastrar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function mensagemDoErro(erro: unknown): string {
  if (erro instanceof ApiError) {
    return erro.codigo === 'DOMINIO_CONFLITO' && erro.detalhes?.usuario !== undefined
      ? 'Este nome de usuário já está em uso.'
      : erro.message;
  }
  return erro instanceof Error ? erro.message : 'Não foi possível salvar o usuário.';
}
